import { Component } from '@theme/component';

/**
 * Square image carousel (sections/image-slide.liquid).
 *
 * - The track is a native horizontal scroller with scroll snapping.
 * - Arrow buttons scroll it by 80% of its visible width.
 * - With `data-auto-slide="<seconds>"` it scrolls on by the same step every <seconds>, and
 *   back to the start once it has reached the end. Auto-slide only runs while the carousel is
 *   on screen, not while keyboard focus is inside it, and not at all with reduced motion.
 *
 * Markup and strings live in the section; this module has no text of its own.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 *
 * @extends {Component<Refs>}
 */
class BondiImageCarousel extends Component {
  requiredRefs = ['track'];

  /** @type {number | undefined} */
  #timer;

  /** @type {AbortController | undefined} */
  #abort;

  /** @type {IntersectionObserver | undefined} */
  #observer;

  #onScreen = false;

  #focused = false;

  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#setUpAutoSlide();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setUpAutoSlide();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#tearDownAutoSlide();
  }

  /** Previous arrow. Called as `on:click="/previous"`. */
  previous() {
    this.#scrollByStep(-1);
  }

  /** Next arrow. Called as `on:click="/next"`. */
  next() {
    this.#scrollByStep(1);
  }

  /** @param {number} direction - 1 forwards, -1 backwards */
  #scrollByStep(direction) {
    const { track } = this.refs;
    track.scrollBy({ left: direction * track.clientWidth * 0.8, behavior: this.#behavior });
  }

  /** @returns {ScrollBehavior} */
  get #behavior() {
    return this.#reducedMotion.matches ? 'auto' : 'smooth';
  }

  get #interval() {
    return Number(this.dataset.autoSlide) * 1000;
  }

  #setUpAutoSlide() {
    this.#tearDownAutoSlide();
    if (!this.#interval) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;
    const update = () => this.#updateTimer();
    this.#reducedMotion.addEventListener('change', update, { signal });
    this.addEventListener('focusin', () => { this.#focused = true; update(); }, { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        // relatedTarget is where focus goes next; null when it leaves the page or goes nowhere.
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        this.#focused = false;
        update();
      },
      { signal }
    );

    this.#observer = new IntersectionObserver(([entry]) => {
      this.#onScreen = Boolean(entry?.isIntersecting);
      this.#updateTimer();
    });
    this.#observer.observe(this);
  }

  #tearDownAutoSlide() {
    this.#abort?.abort();
    this.#abort = undefined;
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#onScreen = false;
    this.#focused = false;
    this.#stopTimer();
  }

  /** Runs the timer only while the carousel is on screen, unfocused and motion is allowed. */
  #updateTimer() {
    const shouldRun = Boolean(this.#interval) && this.#onScreen && !this.#focused && !this.#reducedMotion.matches;

    if (!shouldRun) {
      this.#stopTimer();
    } else if (this.#timer === undefined) {
      this.#timer = window.setInterval(this.#advance, this.#interval);
    }
  }

  #stopTimer() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #advance = () => {
    const { track } = this.refs;
    // The end check reads the position from before this step, so the step at the end
    // rewinds to the start.
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth;
    this.#scrollByStep(1);
    if (atEnd) track.scrollTo({ left: 0, behavior: this.#behavior });
  };
}

if (!customElements.get('bondi-image-carousel')) {
  customElements.define('bondi-image-carousel', BondiImageCarousel);
}
